import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], total: 0 });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(response.data);
    } catch {
      setCart({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1) => {
    if (!user) throw new Error('Please login to add items to cart');
    
    try {
      await axios.post(`${API}/cart`, { product_id: productId, quantity }, { withCredentials: true });
      await fetchCart();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur';
      throw new Error(msg);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!user) return;
    
    try {
      await axios.put(`${API}/cart/${productId}`, { product_id: productId, quantity }, { withCredentials: true });
      await fetchCart();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur';
      throw new Error(msg);
    }
  };

  const removeFromCart = async (productId) => {
    if (!user) return;
    
    await axios.delete(`${API}/cart/${productId}`, { withCredentials: true });
    await fetchCart();
  };

  const clearCart = async () => {
    if (!user) return;
    
    await axios.delete(`${API}/cart`, { withCredentials: true });
    setCart({ items: [], total: 0 });
  };

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, itemCount }}>
      {children}
    </CartContext.Provider>
  );
};
